<template>
    <div class="casino-lobby">

        <div v-if="loading" class="lobby-loading">
            <div class="loading-spinner-simple">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                    </path>
                </svg>
                <p class="loading-text-simple">Loading games...</p>
            </div>
        </div>

        <div v-else class="lobby-sections">
            <!-- Search bar -->
            <div class="lobby-search-bar">
                <div class="lobby-search-inner">
                    <svg class="lobby-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search your game"
                        class="lobby-search-input"
                    />
                    <button v-if="searchQuery" class="lobby-search-clear" @click="searchQuery = ''" aria-label="Clear search">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
            </div>
            <!-- Slots -->
            <section ref="sectionSlots" class="lobby-section">
                <div class="section-header">
                    <div class="section-title-row">
                        <span class="section-icon section-icon-slots">777</span>
                        <h2 class="section-title">Slots</h2>
                    </div>
                    <div class="section-actions">
                        <router-link to="/slots" class="view-all-link">View All</router-link>
                        <button type="button" class="scroll-btn scroll-left" aria-label="Scroll left" @click="scrollSection('slots', -1)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <button type="button" class="scroll-btn scroll-right" aria-label="Scroll right" @click="scrollSection('slots', 1)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                    </div>
                </div>
                <div ref="scrollSlots" class="section-scroll">
                    <div class="section-cards">
                        <SlotsGameCard
                            v-for="game in filteredSectionGames(sectionSlots)"
                            :key="'slots-' + (game.provider_code || '') + '-' + game.code"
                            :game="game"
                            :provider-code="game.provider_code || 'SLOTSGATEWAY'"
                            @launch="onLaunch"
                        />
                    </div>
                </div>
            </section>

            <!-- Live Casino -->
            <section ref="sectionLiveCasino" class="lobby-section">
                <div class="section-header">
                    <div class="section-title-row">
                        <span class="section-icon section-icon-live">📹</span>
                        <h2 class="section-title">Live Casino</h2>
                    </div>
                    <div class="section-actions">
                        <router-link to="/livecasino" class="view-all-link">View All</router-link>
                        <button type="button" class="scroll-btn scroll-left" aria-label="Scroll left" @click="scrollSection('live_casino', -1)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <button type="button" class="scroll-btn scroll-right" aria-label="Scroll right" @click="scrollSection('live_casino', 1)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                    </div>
                </div>
                <div ref="scrollLiveCasino" class="section-scroll">
                    <div class="section-cards">
                        <SlotsGameCard
                            v-for="game in filteredSectionGames(sectionLiveCasino)"
                            :key="'live-' + (game.provider_code || '') + '-' + game.code"
                            :game="game"
                            :provider-code="game.provider_code || 'SLOTSGATEWAY'"
                            @launch="onLaunch"
                        />
                    </div>
                </div>
            </section>

            <!-- Popular -->
            <section ref="sectionPopular" class="lobby-section">
                <div class="section-header">
                    <div class="section-title-row">
                        <span class="section-icon section-icon-popular">★</span>
                        <h2 class="section-title">Popular</h2>
                    </div>
                    <div class="section-actions">
                        <router-link to="/popular" class="view-all-link">View All</router-link>
                        <button type="button" class="scroll-btn scroll-left" aria-label="Scroll left" @click="scrollSection('popular', -1)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <button type="button" class="scroll-btn scroll-right" aria-label="Scroll right" @click="scrollSection('popular', 1)">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                    </div>
                </div>
                <div ref="scrollPopular" class="section-scroll">
                    <div class="section-cards">
                        <SlotsGameCard
                            v-for="game in filteredSectionGames(sectionPopular)"
                            :key="'popular-' + (game.provider_code || '') + '-' + game.code"
                            :game="game"
                            :provider-code="game.provider_code || 'SLOTSGATEWAY'"
                            @launch="onLaunch"
                        />
                    </div>
                </div>
            </section>
        </div>

        <!-- Game modal -->
        <transition name="game-modal-fade">
            <div v-if="gameSession" class="game-modal-overlay" @click="closeGame" role="dialog" aria-modal="true">
                <div class="game-modal" @click.stop role="document">
                    <div class="game-modal-content">
                        <SlotsGameFrame :session="gameSession" />
                    </div>
                    <button type="button" class="game-modal-close-floating" @click.stop="closeGame" aria-label="Close game">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import SlotsGameCard from '@/components/slots/SlotsGameCard.vue';
import SlotsGameFrame from '@/components/slots/SlotsGameFrame.vue';

const SLOTSGATEWAY_PROVIDER_CODE = 'SLOTSGATEWAY';
const LIVE_CASINO_PROVIDERS = ['7mojos', 'betgames', 'evolution', 'playtechlive', 'pragmaticplaylive', 'vivogaming'];

/** Extract provider from any common API field (match store getGameProvider) so Popular filter can match inout/hacksaw/caleta */
function getGameProvider(g, fallback = SLOTSGATEWAY_PROVIDER_CODE) {
    const v = g.provider_code || g.provider_name || g.provider || g.provider_slug || g.category ||
        g.game_provider || g.vendor || g.vendor_name || g.type;
    const s = (v != null && v !== '') ? String(v).trim().toLowerCase() : fallback;
    return s || fallback;
}

function normalizeGame(g) {
    const id = g.id_hash || g.id || g.game_id || g.code;
    const name = g.name || g.game_name || g.title || '';
    const image = g.image || g.banner || g.thumb || g.small_image || g.icon || '';
    const provider = getGameProvider(g, SLOTSGATEWAY_PROVIDER_CODE);
    return {
        code: id,
        name,
        provider_code: provider,
        provider_name: g.provider_name || g.provider || provider,
        image,
        status: g.status != null ? g.status : 1,
    };
}

function isLiveCasinoGame(g) {
    const provider = getGameProvider(g, '');
    const fields = [
        g.category,
        g.game_category,
        g.category_name,
        g.type,
        g.tags,
        g.provider_name,
        g.provider
    ].filter(v => v != null);

    const blob = fields
        .map(v => Array.isArray(v) ? v.join(' ') : String(v))
        .join(' ')
        .toLowerCase();

    if (LIVE_CASINO_PROVIDERS.includes(provider)) return true;
    if (blob.includes('slot') || blob.includes('poker')) return false;
    return blob.includes('live');
}

export default {
    name: 'CasinoLobby',
    components: {
        SlotsGameCard,
        SlotsGameFrame
    },
    data() {
        return {
            activeTab: 'lobby',
            searchQuery: '',
            loading: true,
            sectionSlots: [],
            sectionLiveCasino: [],
            scrollRefs: {}
        };
    },
    computed: {
        ...mapGetters('slots', ['gameSession']),
        scrollStep() {
            return 320;
        },
        /** Popular = games from providers: inout, hacksaw, caleta (from slots + live casino) */
        sectionPopular() {
            const popularProviders = ['inout', 'hacksaw', 'caleta'];
            const getProvider = (g) => (g.provider_code || g.provider_name || g.provider || g.category || '').toString().trim().toLowerCase();
            const slots = this.sectionSlots || [];
            const live = this.sectionLiveCasino || [];
            const fromSlots = slots.filter(g => popularProviders.includes(getProvider(g)));
            const fromLive = live.filter(g => popularProviders.includes(getProvider(g)));
            return [...fromSlots, ...fromLive];
        }
    },
    async mounted() {
        await this.fetchAllSections();
        document.addEventListener('keydown', this.handleKeydown);
        this.$nextTick(() => {
            this.scrollRefs = {
                slots: this.$refs.scrollSlots,
                live_casino: this.$refs.scrollLiveCasino,
                popular: this.$refs.scrollPopular
            };
        });
        const category = this.$route.query.category;
        if (category === 'popular' || category === 'slots' || category === 'live_casino') {
            this.activeTab = category;
            this.$nextTick(() => this.setTab(category));
        }
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown);
        document.body.classList.remove('slot-game-playing');
    },
    methods: {
        ...mapActions('slots', ['resetGameSession']),
        async fetchAllSections() {
            this.loading = true;
            try {
                const [slotsRes, liveRes] = await Promise.all([
                    axios.get('/slotsgateway/games', { params: { currency: 'USD', category: 'slots' } }),
                    axios.get('/slotsgateway/games', { params: { currency: 'USD', category: 'live_casino' } })
                ]);
                const rawSlots = (slotsRes.data.success && slotsRes.data.games) ? slotsRes.data.games.map(normalizeGame) : [];
                const rawLive = (liveRes.data.success && liveRes.data.games) ? liveRes.data.games.map(normalizeGame) : [];

                // Defensive partition: API can return mixed lists. Ensure lobby sections stay correct.
                this.sectionSlots = rawSlots.filter(g => !isLiveCasinoGame(g));
                this.sectionLiveCasino = rawLive.filter(g => isLiveCasinoGame(g));
            } catch (e) {
                console.error('Casino lobby fetch error:', e);
                this.sectionSlots = [];
                this.sectionLiveCasino = [];
            } finally {
                this.loading = false;
            }
        },
        filteredSectionGames(list) {
            if (!list || !list.length) return [];
            if (!this.searchQuery.trim()) return list;
            const q = this.searchQuery.toLowerCase().trim();
            return list.filter(g =>
                (g.name && g.name.toLowerCase().includes(q)) ||
                (g.code && g.code.toLowerCase().includes(q))
            );
        },
        setTab(tab) {
            this.activeTab = tab;
            if (tab === 'lobby') {
                this.$el.querySelector('.lobby-sections')?.scrollIntoView({ behavior: 'smooth' });
                return;
            }
            const refMap = { slots: 'sectionSlots', live_casino: 'sectionLiveCasino', popular: 'sectionPopular' };
            const refName = refMap[tab];
            const el = refName ? this.$refs[refName] : null;
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        scrollSection(section, direction) {
            const el = this.scrollRefs[section];
            if (!el) return;
            const amount = this.scrollStep * direction;
            el.scrollBy({ left: amount, behavior: 'smooth' });
        },
        onLaunch() {
            const sidebar = document.querySelector('aside#sidebar-left');
            if (sidebar && !sidebar.classList.contains('collapsed') && window.innerWidth > 1024) {
                const btn = sidebar.querySelector('.menu-toggle');
                if (btn) btn.click();
            }
        },
        closeGame() {
            this.resetGameSession();
        },
        handleKeydown(e) {
            if (e.key === 'Escape' && this.gameSession) this.closeGame();
        }
    },
    watch: {
        gameSession(v) {
            document.body.classList.toggle('slot-game-playing', !!v);
        }
    }
};
</script>

<style scoped>
/* ── Container ─────────────────────────────────────────────────── */
.casino-lobby {
    width: 100%;
    max-width: 100%;
    min-height: calc(100vh - 80px);
    padding: 28px 32px 40px;
    box-sizing: border-box;
}

/* ── Loading ────────────────────────────────────────────────────── */
.lobby-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 320px;
}

.loading-spinner-simple {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.loading-spinner-simple svg { color: var(--accent-yellow); }

.loading-text-simple {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

/* ── Search bar ─────────────────────────────────────────────────── */
.lobby-search-bar {
    margin-bottom: 32px;
}

.lobby-search-inner {
    position: relative;
    display: flex;
    align-items: center;
}

.lobby-search-icon {
    position: absolute;
    left: 16px;
    color: rgba(255, 255, 255, 0.4);
    pointer-events: none;
    flex-shrink: 0;
}

.lobby-search-input {
    width: 100%;
    height: 50px;
    padding: 0 44px 0 48px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 500;
    color: #fff;
    background: rgba(255, 255, 255, 0.06);
    border: 1.5px solid rgba(255, 255, 255, 0.12);
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    font-family: var(--font-main);
}

.lobby-search-input::placeholder {
    color: rgba(255, 255, 255, 0.38);
}

.lobby-search-input:focus {
    border-color: var(--color-copper, #d4a574);
    background: rgba(255, 255, 255, 0.09);
}

.lobby-search-clear {
    position: absolute;
    right: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    border: none;
    cursor: pointer;
    transition: background 0.15s;
}

.lobby-search-clear:hover { background: rgba(255, 255, 255, 0.22); }

/* ── Sections ───────────────────────────────────────────────────── */
.lobby-sections {
    display: flex;
    flex-direction: column;
    gap: 36px;
}

.lobby-section { scroll-margin-top: 24px; }

/* ── Section header ─────────────────────────────────────────────── */
.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}

.section-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.section-icon {
    font-size: 20px;
    font-weight: 800;
    color: var(--color-copper, #d4a574);
    line-height: 1;
}

.section-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
    color: #fff;
    letter-spacing: 0.2px;
}

.section-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.view-all-link {
    font-size: 13px;
    font-weight: 600;
    color: var(--color-copper, #d4a574);
    text-decoration: none;
    white-space: nowrap;
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid rgba(212, 165, 116, 0.3);
    transition: background 0.15s;
}

.view-all-link:hover {
    background: rgba(212, 165, 116, 0.12);
}

/* ── Circular scroll buttons ────────────────────────────────────── */
.scroll-btn {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1.5px solid rgba(255, 255, 255, 0.18);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, transform 0.15s;
    flex-shrink: 0;
}

.scroll-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.35);
    transform: scale(1.08);
}

/* ── Horizontal scroll strip ────────────────────────────────────── */
.section-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 6px;
}

.section-scroll::-webkit-scrollbar { display: none; }

.section-cards {
    display: flex;
    gap: 14px;
    width: max-content;
    padding: 4px 2px;
}

/* Card sizing inside carousel – 180px wide, natural 3:4 ratio */
.section-cards :deep(.game-card) {
    flex: 0 0 175px;
    width: 175px;
    min-width: 175px;
    aspect-ratio: 3 / 4;
    max-height: none;
}

/* ── Game modal ─────────────────────────────────────────────────── */
.game-modal-fade-enter-active,
.game-modal-fade-leave-active { transition: opacity 0.25s ease; }
.game-modal-fade-enter-from,
.game-modal-fade-leave-to { opacity: 0; }

.game-modal-overlay {
    position: fixed !important;
    inset: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: #000;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    z-index: 9999999 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
}

.game-modal {
    position: relative;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    background: #000;
    border: none;
    border-radius: 0;
    overflow: hidden;
    z-index: 10000000;
}

.game-modal-content {
    position: absolute;
    inset: 0;
    overflow: hidden;
}

.game-modal-close-floating {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    width: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    background: rgba(0, 0, 0, 0.6) !important;
    border: 2px solid rgba(255, 255, 255, 0.25) !important;
    border-radius: 50% !important;
    color: #fff !important;
    cursor: pointer !important;
    z-index: 10000003 !important;
    transition: background 0.2s, border-color 0.2s;
}

.game-modal-close-floating:hover {
    background: rgba(0, 0, 0, 0.85) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
}

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 768px) {
    .casino-lobby { padding: 20px 16px 32px; }

    .section-title { font-size: 18px; }

    .section-cards :deep(.game-card) {
        flex: 0 0 148px;
        width: 148px;
        min-width: 148px;
    }
}

@media (max-width: 480px) {
    .casino-lobby { padding: 16px 12px 28px; }

    .section-title { font-size: 16px; }

    .section-cards :deep(.game-card) {
        flex: 0 0 130px;
        width: 130px;
        min-width: 130px;
    }
}
</style>
